import { useContext, useState, useEffect, useRef } from 'react';
import { Modal, Tooltip } from 'bootstrap';
import { useNavigate } from 'react-router-dom';
import AdaptiveLogo from '../../../../common/controls/AdaptiveLogo';
import { getCompanyImage, getUniversityImage } from '../../../../common/styles/Images';
import { SmartContext } from '../../../../library/Core/SmartContext';
import { axiosClient } from '../../../../library/Service/axiosClient';
import VerifyMobileControl from '../../onboarding/VerifyMobileControl';
import {
    allowPerformAction,
    canEditProfile,
    getActionElementClass,
    getDomainValueForCode,
    getFlexColDomainValueForCode,
    getProfileMessagingDisabledTooltip,
    isEmpty,
    isProfileMessagingDisabled,
    isSchoolAdmin,
    isSuperAdmin,
} from '../../../../library/Core/SmartFunctions';
import { getProfileHeadeData } from '../../../../library/Service/cardTypeServce';
import CurrentCompaniesCarousel, { getCurrentCompanySlides } from './CurrentCompaniesCarousel';
import ShareActionModal from '../../../management/alumni-list/ShareActionModal';
import ShareEmailActionModal from '../../../management/alumni-list/ShareEmailActionModal';
import SessionContext from '../../../../library/Core/SessionContext';
import AlumniRecognitionStatus from './AlumniRecognitionStatus';
import ProfileWatchlistBadge from './ProfileWatchlistBadge';
import InitialsAvatar, { hasProfilePicture } from '../../../../library/SimpleControls/InitialsAvatar';
import './ProfileHeader.css';

const ProfileHeader = () => {
    const { state } = useContext(SmartContext);
    const navigate = useNavigate();
    const { sessionState, sessionDispatch } = useContext(SessionContext);
    const [profileMarkerURL, setProfileMarkerURL] = useState('');
    const [showProfileMarkerAlert, setShowProfileMarkerAlert] = useState(false);
    const [profileMarkerAlertMessage, setprofileMarkerAlertMessage] = useState('');
    const [isMentor, setIsMentor] = useState(false);
    const [isRecruiter, setIsRecruiter] = useState(false);
    const [isDonor, setIsDonor] = useState(false);
    const [isServiceProvider, setIsServiceProvider] = useState(false);

    const setProfileMarkerAlertData = () => {
        if(!isEmpty(JSON.parse(state.data?.profile?.profileMarkerJson)))
        {
            const demographicsMarkerColumns = ["CurrentlyAbroad","genderCode","LivedAbroadandBack","StudiedAbroad"]
            const WorkProfileMarkerColumns = [
                "is_leadership_role_in_large_company",
                "is_leadership_role_in_a_listed_company",
                "is_leadership_role_in_sme_company",
                "is_growing_startup_founder",
                "is_mid_senior_role_in_fortune_company",
                "work_experience"
            ]
            let urls:string[] = [];
            const markersJson = JSON.parse(state.data?.profile?.profileMarkerJson);
            let messageColumnCategories:string[] = [];
            markersJson.forEach((marker: any) => {
                if (demographicsMarkerColumns.includes(marker.field) && !messageColumnCategories.includes('Demographics') ) {
                    messageColumnCategories.push('Demographics');
                    if(marker.field == 'StudiedAbroad'){
                        urls.push('pg');
                    }else{
                        urls.push('info');
                    }
                }

                if (WorkProfileMarkerColumns.includes(marker.field) && !messageColumnCategories.includes('Work Profile') ) {
                    messageColumnCategories.push('Work Profile');
                    urls.push('work');
                }
            });
            
            const message = `You have a ${messageColumnCategories.join(',')} Marker active without substantial data in the actual profile of the alumni.`
            setShowProfileMarkerAlert(false);
            if(urls.includes('work'))
            {
                setProfileMarkerURL(`/alumni/${model.id}/profile/work`);
            }
            else
            {
                if(urls.includes("pg") && urls.includes("info"))
                {
                    setProfileMarkerURL(`/alumni/${model.id}/info`);
                }
                else if(urls.includes("pg"))
                {
                    setProfileMarkerURL(`/alumni/${model.id}/pg`);
                }
                else
                {
                    setProfileMarkerURL(`/alumni/${model.id}/info`);
                }
            }
            
            setprofileMarkerAlertMessage(message);
        }
    };

    useEffect(() => {
        const hasValidRole = ['ALUMNI', 'STUDENT'].includes(sessionState?.userTypeCode ?? '');

        if (hasValidRole && (state?.data?.id !== sessionState?.id)) {
            axiosClient().post(
            `${process.env.REACT_APP_COMMON_API}/users/update-user-profile-view-log/${state?.data?.id}`
            );
        }

        setProfileMarkerAlertData();

        // const markersJson = JSON.parse(state.data?.profile?.profileMarkerJson);
        const enabledProfileMarkers = state.data.enabledProfileMarkers || [];
        const recruiterMarkers = ['recruitedInPast', 'recruitingFromOtherInstitutes']
        if (
            state?.data?.profile?.recruiterProfile === 'MARKED_AS_RECRUITER'
            || (enabledProfileMarkers?.length > 0 && recruiterMarkers.some((marker) => enabledProfileMarkers.includes(marker)))
        ) {
            setIsRecruiter(true);
        }

        if ( 
            state?.data?.mentoringInterests?.interestedInWebinar || state?.data?.mentoringInterests?.shareMyStory
            || state?.data?.isEventPresenter == 1 || state?.data?.hasGuidanceVideo == 1
            || state?.data?.messageCount > 0 || state?.data?.qnaId > 0
        ) {
            setIsMentor(true);
        }

        // if any of donationPledges, donatedInPast keys found in markers, set isDonor true
        const donationMarkerColumns = ["donationPledges", "donatedInPast"];

        if (enabledProfileMarkers && enabledProfileMarkers.length > 0) {
            const enabledDonationMarkers = enabledProfileMarkers.filter((marker: any) => donationMarkerColumns.includes(marker));
            if (enabledDonationMarkers.length > 0) {
                setIsDonor(true);
            }
        }

    }, [state?.data?.userInstitutes]);
   
    const getProgramDetails = () => {
        const institutes = state?.data?.userInstitutes || [];
        if (!institutes.length) return null;

        const primaryInstitute =
            state.data.selectedInstitute ||
            institutes.find((inst: any) => inst.instituteId == sessionState?.primaryInstitute) ||
            institutes[0];

        const primaryProgram = getDomainValueForCode(primaryInstitute?.programId,'ALL_PROGRAM_CODE',state) || "";
        const primaryPassOut = primaryInstitute?.passOutYear || "";
        const primaryInstituteName = getDomainValueForCode(primaryInstitute?.instituteId,'INSTITUTE_DOMAIN',state) || "";

        const otherInstitutes = institutes.filter(
            (inst: any) => inst.instituteId !== primaryInstitute.instituteId
        );

        const extraCount = otherInstitutes.length;

        const tooltipContent = otherInstitutes
            .map(
            (inst: any) =>  {
                const program = getDomainValueForCode(inst?.programId,'ALL_PROGRAM_CODE',state) || "";
                const passOut = inst?.passOutYear || "";
                const instituteName = getDomainValueForCode(inst?.instituteId,'INSTITUTE_DOMAIN',state) || "";
                return `${program}, ${passOut} ${instituteName}`
            }
                
            )
            .join(" | ");

        return (
            <>
            Program: {`${primaryProgram}, ${primaryPassOut}`}<br/>{`${primaryInstituteName}`}
            
            {extraCount > 0 && (
                <div
                data-bs-toggle="tooltip"
                data-bs-original-title={tooltipContent}
                className="alumni_profile_extra_institute"
                >
                + {extraCount} More
                </div>
            )}
            </>
        );
    };
    

    useEffect(() => {
        var tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
        var tooltipList = tooltipTriggerList.map(function (tooltipTriggerEl) {
            return new Tooltip(tooltipTriggerEl);
        });
    }, []);
    //const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
    const [selectedStudents, setSelectedStudents] = useState([]);
    useEffect(() => {
        console.log(selectedStudents, 'selectedStudents after state update');
    }, [selectedStudents]); // The effect triggers when selectedStudents changes

    const model = state.internal.model;
    const data = state.data;
    const isMessagingDisabled = isProfileMessagingDisabled(state?.data, sessionState?.primaryInstitute);

    console.log(data, 'data');

    const nameRef = useRef<HTMLSpanElement>(null);
    const [isNameTruncated, setIsNameTruncated] = useState(false);

    useEffect(() => {
        const el = nameRef.current;
        if (!el) return;
        setIsNameTruncated(el.scrollWidth > el.clientWidth);
    }, [model.firstName, model.lastName]);

    useEffect(() => {
        if (!isNameTruncated || !nameRef.current) return;
        const tooltip = new Tooltip(nameRef.current);
        return () => tooltip.dispose();
    }, [isNameTruncated]);

    const [showVerifyMobile, setShowVerifyMobile] = useState(false);
    const [isMobileVerified, setIsMobileVerified] = useState(model.isMobileVerified); // Initialize the state based on model.is_mobile_verify
    const handleVerifyMobile = (event: any) => {
        event.preventDefault();
        sendMobileInvite(model.mobileNumber);
        setShowVerifyMobile(true);
    };
    const socialShare = async (students: any) => {
        try {
            // Your logic here
            //console.log('updateAction logic');
            console.log(students, 'students data');
            setSelectedStudents(students);

            console.log(selectedStudents, 'selectedStudents data');
            const modalElement = document.getElementById('social-share');
            if (modalElement) {
                const modal = new Modal(modalElement);
                modal.show();
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            alert(error);
        }
    };

    const socialEmailShare = async (students: any) => {
        try {
            // Your logic here
            //console.log('updateAction logic');
            console.log(students, 'students data');
            setSelectedStudents(students);
            console.log(selectedStudents, 'selectedStudents data');
            const modalElement = document.getElementById('social-email');
            if (modalElement) {
                const modal = new Modal(modalElement);
                modal.show();
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            alert(error);
        }
    };

    const sendMobileInvite = async (mobile: string) => {
        try {
            const response = await axiosClient().post(`${process.env.REACT_APP_COMMON_API}/sms/send-verification-otp`);
            if (response?.status === 200) {
                setIsMobileVerified(true);
            }
        } catch (error) {
            console.error('Error sending SMS:', error);
        }
    };

    const profileImage = hasProfilePicture(model?.profileImage) ? model.profileImage : '';

    const handleSendMessage = (event: any, id: string) => {
        event.preventDefault();
        if (!allowPerformAction()) return;
        navigate(`/messages/${id}`);
    };

    const {
        current_logo,
        current_name,
        current_field,
        current_type_text,
        current_status,
        showCurretnStatusDiv,
        interestedInUpSkilling,
        interestedInHigherEducation,
    } = getProfileHeadeData(state, model);
    const showCompanySlider =
        getCurrentCompanySlides(state).length > 1 &&
        (current_type_text === 'CURRENT COMPANY' ||
            current_type_text === 'CREDITED AS' ||
            state?.data?.profile?.workingStatusCode === 'PROFESSIONAL_PRACTICE');
    console.log("current_status",current_status)

    const portfolioLink: string = state?.data?.profile?.portfolioLink || '';
    const hasFreelancingRole =
        String(current_status || '').toLowerCase() === 'self employed and freelancing' ||
        (state?.data?.jobsList ?? []).some(
            (job: any) => (job?.isCurrent === true || job?.isCurrent === 1) && job?.jobCategory === 'SELF_EMPLOYED_AND_FREELANCING'
        );
    const portfolioUrl =
        hasFreelancingRole && !isEmpty(portfolioLink)
            ? portfolioLink.startsWith('http') ? portfolioLink : `https://${portfolioLink}`
            : '';

    const [isOpen, setOpen] = useState(false);

    const handleFilterToggle = () => {
        if (!allowPerformAction()) return;
        setOpen(!isOpen);
    };

    const isProfilePictureEditable = data.id === sessionState?.id ? true : false;
    const isInstituteAdmin = sessionState?.userTypeCode === 'INSTITUTE_ADMIN';
    // The watchlist report's backend query inner-joins on `institute.commitment_paths IS NOT NULL` —
    // an institute with no commitment path assigned always returns zero rows, for every layer. Skip
    // the fetch entirely in that case. Check the ALUMNUS's own institute (selectedInstitute — the
    // same one the fetch itself is scoped to via filterInstituteId), not the admin's own, since a
    // group admin's institute and the alumnus's institute can differ. Prefer `school_settings`
    // (written fresh on every login) when it happens to match that institute — it's fresher than the
    // INSTITUTE_DOMAIN snapshot, which only refreshes via a nightly batch job — otherwise fall back
    // to the domain snapshot for that same institute id.
    const hasCommitmentPaths = (() => {
        const targetInstituteId = state?.data?.selectedInstitute?.instituteId ?? sessionState?.primaryInstitute;
        try {
            const flexColStr = getFlexColDomainValueForCode(String(targetInstituteId), 'INSTITUTE_DOMAIN', state);
            return Array.isArray(flexColStr ? JSON.parse(flexColStr as string)?.commitment_paths : null);
        } catch {
            return false;
        }
    })();
    console.log("hasCommitmentPaths",hasCommitmentPaths);
    return (
        <>
            {!state?.flags.isDataLoading && (
                <section
                    key={state?.routeInfo?.id}
                    className="bg-white container-fluid alumni_profile_brife_wrap"
                    id="alumni_profile_brife_wrap">
                    <div className="max-1140 mx-auto alumni_profile_brife">
                        <div className="pb-4">
                            <h1 className="mb-1 font-22 font-700 mb-0">Great Job!</h1>
                            <p className="font-18">Keep your journey updated and inspire others through your career milestones!</p>
                            <hr className="opacity-25" />
                        </div>
                        <div className="row">
                            {state?.data?.userTypeCode !== 'STUDENT' && !canEditProfile(model.id, sessionState) && (
                                <div className="col-12 mb-4">
                                    <a className="btn-back" role="button" onClick={() => navigate('/alumni/directory')}>
                                        <i className="icon-right-arrow"></i> <span>Back to Directory</span>
                                    </a>
                                </div>
                            )}

                            {showProfileMarkerAlert && isSchoolAdmin() && <div className='col-12'>
                                <div className='custom-alret'>
                                    <div className='row g-2 align-items-center'>
                                        <div className="col">{profileMarkerAlertMessage}</div>
                                        <div className='col-auto'><a onClick={() => navigate(profileMarkerURL)} className='btn-green'><span>Update Now</span></a></div>
                                        <div className='col-auto'><button onClick={() => setShowProfileMarkerAlert(false)} className='btn-close w-20 mt-2'></button></div>
                                    </div>
                                </div>
                            </div>}
                            <div className="col-12">
                                <div className="alumni_profile_name_college_other_detail_wrap">
                                    <div className="alumni_profile_image_name_other_detail">
                                        <div className="alumni_profile_image_wrap d-flex flex-column">
                                            <div className="alumni_profile_image">
                                                {profileImage ? (
                                                    <img src={profileImage} alt="" />
                                                ) : (
                                                    <InitialsAvatar firstName={model?.firstName} lastName={model?.lastName} fill />
                                                )}
                                            </div>
                                            {(isSchoolAdmin() || isProfilePictureEditable) && (
                                                <a
                                                    className={`pencil-edit ${getActionElementClass()}`}
                                                    href="javascript:;"
                                                    onClick={() => {
                                                        if (allowPerformAction()) {
                                                            navigate(`/alumni/${model.id}/profile-picture`);
                                                        }
                                                    }}>
                                                    <i className="icon-pencil"></i> {/* Optional: Add an icon */}
                                                </a>
                                            )}
                                            <div className="mt-2">
                                                {isInstituteAdmin && hasCommitmentPaths && !state?.data?.instituteSelectionPending && (
                                                        <ProfileWatchlistBadge
                                                            studentId={model.id}
                                                            instituteId={sessionState?.primaryInstitute}
                                                            filterInstituteId={state?.data?.selectedInstitute?.instituteId}
                                                        />
                                                    )}
                                            </div>
                                        </div>
                                        <div className="alumni_profile_name_other_detail">
                                            <h2 className="d-flex gap-2 align-items-center mb-1">
                                                        <span
                                                            ref={nameRef}
                                                            className="text-truncate "
                                                            // style={{ display: 'block', maxWidth: '136px' }}
                                                            {...(isNameTruncated
                                                                ? { 'data-bs-toggle': 'tooltip', 'data-bs-original-title': `${model.firstName} ${model.lastName ? model.lastName : ''}` }
                                                                : {})}>
                                                            {model.firstName} {model.lastName ? model.lastName : ''}
                                                        </span>
                                                        {model.isEditable && (
                                                            <a
                                                                className={`btn btn-link p-0 d-flex align-items-center ${getActionElementClass()}`}
                                                                role="button"
                                                                onClick={() => {
                                                                    if (allowPerformAction()) {
                                                                        navigate(`/alumni/${model.id}/info`);
                                                                    }
                                                                }}>
                                                                <span className="pencil-edit" style={{ position: 'static' }}></span>
                                                            </a>
                                                        )}
                                                    </h2>
                                            <div className="alumni_profile_location">
                                                <i className="icon-location-pin"></i>{' '}
                                                <span>
                                                    {model.cityName}, {model.countryName}
                                                </span>
                                            </div>

                                            {state?.data?.profile?.recruiterProfile === 'MARKED_AS_RECRUITER' && (
                                                <div className="verify_massrecruiter ms-0 mb-3 w-100">
                                                    <ul className="d-flex gap-3 align-items-center flex-wrap">
                                                    <li>
                                                        <i className="verify"></i>
                                                        <span>Confirmed </span>
                                                    </li>

                                                    {Array.isArray(state?.data?.recruiterJobs) && state.data.recruiterJobs.length > 0 && (() => {
                                                        const hasMass = state.data.recruiterJobs.some(
                                                        (job:any) => job.expectedNumbersOfOffers === '>10'
                                                        );
                                                        const hasNiche = state.data.recruiterJobs.some(
                                                        (job:any) => ['<5', '5_10'].includes(job.expectedNumbersOfOffers)
                                                        );

                                                        if (hasMass) {
                                                        return (
                                                            <li>
                                                            <i className="massrecruiter"></i>
                                                            <span>Mass Recruiter</span>
                                                            </li>
                                                        );
                                                        } else if (hasNiche) {
                                                        return (
                                                            <li>
                                                            <i className="massrecruiter"></i>
                                                            <span>Niche Recruiter</span>
                                                            </li>
                                                        );
                                                        } else {
                                                        return null;
                                                        }
                                                    })()}
                                                    </ul>
                                                </div>
                                                )}
                                                
                                            <div className="alumni_profile_class_college">
                                               <div className="alumni_profile_class">
                                                    {getProgramDetails()}
                                                </div>
                                                {/* <div className="alumni_profile_college mb-2">{model?.schoolName ?? userInstituteName}</div> */}

                                                {model.isEditable && (
                                                    <div className="alumni_profile_college_phone mb-1">
                                                        <span className="font-16 font-500 text-black line-height-18">
                                                            (+{model?.mobileCountryCode}) {model?.mobileNumber}
                                                        </span>
                                                        {/* {userTypeCode != 'STUDENT' && model?.mobileNumber?.length > 0 && !isMobileVerified && (
                                                        <a className="verifyLink" role="button" onClick={handleVerifyMobile}>
                                                            <span>Verify Now</span>
                                                        </a>
                                                    )} */}
                                                        {/* {isMobileVerified && (
                                                            <a className="verifiedLink">
                                                                <i className="icon-varified font-16 me-2"></i>
                                                                <span>Verified</span>
                                                            </a>
                                                        )} */}
                                                    </div>
                                                )}

                                                {model.isEditable && (
                                                    <div className="alumni_profile_college_phone">
                                                        <span
                                                            data-bs-toggle="tooltip"
                                                            className="font-16 font-500 text-black line-height-18"
                                                            data-bs-original-title={state?.data?.profile?.contactEmailId}
                                                            data-bs-placement="bottom">
                                                            {state?.data?.profile?.contactEmailId?.length > 30
                                                                ? state?.data?.profile?.contactEmailId.substring(0, 15) + '...'
                                                                : state?.data?.profile?.contactEmailId}
                                                        </span>
                                                        {/* {state?.data?.isEmailVerified == 1 && <a className="verifiedLink">
                                                            <i className="icon-varified font-16 me-2"></i>
                                                            <span>Verified</span>
                                                        </a>} */}
                                                    </div>
                                                )}
                                            </div>
                                            {isSchoolAdmin() && (
                                                <div className="send_link">
                                                    <div className="row g-2">
                                                        <div className="col-auto">
                                                            {isMessagingDisabled ? (
                                                                <a
                                                                    href="javascript:;"
                                                                    className="btn-send btn-white-tooltip"
                                                                    style={{ background: 'grey' }}
                                                                    data-bs-toggle="tooltip"
                                                                    data-bs-html="true"
                                                                    data-bs-original-title={getProfileMessagingDisabledTooltip(state?.data)}>
                                                                    <i className="icon-alumni-send"></i>
                                                                    <span>Send</span>
                                                                </a>
                                                            ) : (
                                                                <a
                                                                    role="button"
                                                                    onClick={(event) => handleSendMessage(event, model.id)}
                                                                    className={`btn-send ${getActionElementClass()}`}>
                                                                    <i className="icon-alumni-send"></i>
                                                                    <span>Send</span>
                                                                </a>
                                                            )}
                                                        </div>
                                                        <div className="col-auto">
                                                            <div className="w-auto position-relative">
                                                                <a
                                                                    className={`btn-moreaction px-3 collapsed ${getActionElementClass()}`}
                                                                    href="javascript:;"
                                                                    onClick={handleFilterToggle}
                                                                    aria-expanded={isOpen ? 'true' : 'false'}
                                                                    aria-controls="collapseExample">
                                                                    <span>More Actions</span>
                                                                    <span className="dot">
                                                                        <i></i>
                                                                    </span>
                                                                </a>
                                                                <div
                                                                    className={`position-absolute z1 top-48 left-0 collapse ${
                                                                        isOpen ? 'show' : ''
                                                                    }`}
                                                                    id="collapseExample">
                                                                    <div className="card card-body">
                                                                        <ul className="more_action">
                                                                            <li>
                                                                                <a
                                                                                    href="javascript:;"
                                                                                    role="button"
                                                                                    onClick={(event) => socialShare(model.id)}>
                                                                                    Social Share
                                                                                </a>
                                                                            </li>
                                                                            <li>
                                                                                <a
                                                                                    href="javascript:;"
                                                                                    role="button"
                                                                                    onClick={(event) => socialEmailShare(model.id)}>
                                                                                    Send via Email
                                                                                </a>
                                                                            </li>
                                                                        </ul>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                   
                                    {showCurretnStatusDiv && (
                                        <div className={`alumni_profile_college_name_other_detail_wrap${showCompanySlider ? ' has-company-slider' : ''}`}>
                                            {showCompanySlider ? (
                                                <CurrentCompaniesCarousel state={state} />
                                            ) : (
                                            <AdaptiveLogo
                                                className="alumni_profile_college_logo"
                                                src={current_logo}
                                                alt={current_name}
                                                fallbackShape={
                                                    /college|school|university|doctoral|pursuing ug|pursuing pg/i.test(
                                                        `${current_type_text || ''} ${current_status || ''}`
                                                    )
                                                        ? 'circle'
                                                        : 'square'
                                                }
                                            />
                                            )}
                                            <div className={`alumni_profile_college_name_other_detail${showCompanySlider ? ' w-100' : ''}`}>
                                                {!showCompanySlider && (
                                                <div className="alumni_profile_college_name_detail">
                                                    <div className="alumni_profile_college_joined">{current_type_text}</div>
                                                    <h3>{current_name}</h3>
                                                    {current_field && (
                                                        <div className="alumni_profile_college_subject">
                                                            <i className="icon"></i>
                                                            <span className="defaultTip" data-bs-toggle="tooltip" title={current_field}>
                                                                {current_field}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                )}
                                                <div className={`alumni_profile_other_detail${showCompanySlider ? ' w-100' : ''}`}>
                                                    <ul className={`alumni_profile_status${showCompanySlider ? ' profile-header-status' : ''}`}>
                                                        <li>
                                                            <span className="title">Current Status</span>
                                                            <p>{current_status}</p>
                                                            {!showCompanySlider && portfolioUrl && (
                                                                <div className="alumni_profile_name_other_detail w-auto">
                                                                    <a href={portfolioUrl} target="_blank" rel="noreferrer" role="button" className="btn-send w-auto">
                                                                        <span>View Portfolio</span>
                                                                    </a>
                                                                </div>
                                                            )}
                                                        </li>
                                                        {showCompanySlider && portfolioUrl && (
                                                            <li>
                                                                <span className="title">Portfolio</span>
                                                                <div className="alumni_profile_name_other_detail profile-header-action">
                                                                    <a href={portfolioUrl} target="_blank" rel="noreferrer" role="button" className="btn-send">
                                                                        <span>View Portfolio</span>
                                                                    </a>
                                                                </div>
                                                            </li>
                                                        )}
                                                        {(interestedInUpSkilling || interestedInHigherEducation) && (
                                                            <li>
                                                                <span className="title">Next Move in Career</span>
                                                                <p>
                                                                    {interestedInHigherEducation && 'Higher studies'}
                                                                    {interestedInHigherEducation &&
                                                                        interestedInUpSkilling &&
                                                                        ', Upskilling courses'}
                                                                    {!interestedInHigherEducation &&
                                                                        interestedInUpSkilling &&
                                                                        'Upskilling courses'}
                                                                </p>
                                                            </li>
                                                        )}

                                                        {isSchoolAdmin() == false && sessionState?.id !== model.id && (
                                                            <li>
                                                                <span className="title">Connect Now</span>
                                                                <div className={`alumni_profile_name_other_detail ${showCompanySlider ? 'profile-header-action' : 'w-100'}`}>
                                                                    <a onClick={() => navigate(`/messages/${model.id}`)} role="button" className="btn-send">
                                                                        <i className="icon-send"></i><span>Send</span>
                                                                    </a>
                                                                </div>
                                                            </li>
                                                        )}
                                                    </ul>
                                                </div>
                                            </div>
                                            <div className="w-100 mt-4">
                                                <div className="row g-4">                                

                                                    <div className="col-auto"><div className={`d-flex flex-wrap align-items-center gap-2 ${!isMentor ? 'opacity-25 filter-b-0' : ''}`}><img src="https://s3.ap-south-1.amazonaws.com/media.univariety.com/userreactapp/images/icon-green-mentor.svg" width="24" className="" alt=""/> <span>Mentor</span></div></div>

                                                    <div className="col-auto"><div className={`d-flex flex-wrap align-items-center gap-2 ${!isRecruiter ? 'opacity-25 filter-b-0' : ''}`}><img src="https://s3.ap-south-1.amazonaws.com/media.univariety.com/userreactapp/images/icon-green-recruiter.svg" width="24" className="" alt="" /> <span>Recruiter</span></div></div>

                                                    <div className="col-auto"><div className={`d-flex flex-wrap align-items-center gap-2 ${!isDonor ? 'opacity-25 filter-b-0' : ''}`}><img src="https://s3.ap-south-1.amazonaws.com/media.univariety.com/userreactapp/images/icon-green-wallet.svg" width="24" className="" alt="" /> <span>Donor</span></div></div>
                                                    <div className="col-auto"><div className={`d-flex flex-wrap align-items-center gap-2 ${!isServiceProvider ? 'opacity-25 filter-b-0' : ''}`}><img src="https://s3.ap-south-1.amazonaws.com/media.univariety.com/userreactapp/images/icon-green-briefcase.svg" width="24" className="" alt="" /> <span>Service Provider</span></div></div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}
            {showVerifyMobile && <VerifyMobileControl setShowVerifyMobile={setShowVerifyMobile} mobileNumber={model.mobileNumber} />}
            <ShareActionModal data={[selectedStudents]} />
            <ShareEmailActionModal data={[selectedStudents]} />
        </>
    );
};

export default ProfileHeader;
